import React, { useState, useEffect } from "react";
import { Button, Paper, Typography, Box } from "@mui/material";
import { TextField } from "@mui/material";
import { useStore } from "../../app/stores/store";
import { useTranslation } from "react-i18next";
import agent from "../../app/api/agent";

function buildDefaultHeader(logoUrl: string, orgName: string, address: string, email: string, phone: string) {
    const logoTag = logoUrl
        ? `<img src="${logoUrl}" alt="Logo" height="50" />`
        : "";
    return `<table width="100%" style="border-bottom:1px solid #eee;"><tr>
<td align="left" style="padding:10px">
  ${logoTag}
</td>
<td align="right" style="padding:10px;font-size:14px;color:#555;">
    ${orgName}<br/>
    ${address}<br/>
    ${email}<br/>
    ${phone}
</td>
</tr></table>`;
}

function buildDefaultFooter(orgName: string, address: string, email: string, phone: string) {
    return `<div style="border-top:1px solid #eee;padding:10px 0 0 0;font-size:12px;color:#888;text-align:center;">
    ${orgName} | ${address} | ${email} | ${phone}<br/>
    ©${new Date().getFullYear()} ${orgName}
</div>`;
}

const FALLBACK_HEADER = `<table width="100%" style="border-bottom:1px solid #eee;"><tr>
<td align="left" style="padding:10px"></td>
<td align="right" style="padding:10px;font-size:14px;color:#555;"></td>
</tr></table>`;

const FALLBACK_FOOTER = `<div style="border-top:1px solid #eee;padding:10px 0 0 0;font-size:12px;color:#888;text-align:center;"></div>`;

export default function EmailLayoutSettingsPage() {
    const emailTemplateStore = useStore((s) => s.emailTemplateStore);
    const { t } = useTranslation();
    const [header, setHeader] = useState<string>(FALLBACK_HEADER);
    const [footer, setFooter] = useState<string>(FALLBACK_FOOTER);

    useEffect(() => {
        (async () => {
            let defaultHeader = FALLBACK_HEADER;
            let defaultFooter = FALLBACK_FOOTER;

            try {
                const tenant = await agent.Tenants.getCurrent();
                const logoUrl = tenant?.logo ?? "";
                const orgName = tenant?.title || tenant?.name || "";
                const address = [tenant?.street, tenant?.zipCode, tenant?.city].filter(Boolean).join(", ");
                const email = tenant?.contactEmail ?? "";
                const phone = tenant?.contactPhone ?? "";
                defaultHeader = buildDefaultHeader(logoUrl, orgName, address, email, phone);
                defaultFooter = buildDefaultFooter(orgName, address, email, phone);
            } catch {
                // Use fallback if tenant info unavailable
            }

            const settings = await emailTemplateStore.fetchLayoutSettings();
            if (settings && (settings.header || settings.footer)) {
                setHeader(settings.header || defaultHeader);
                setFooter(settings.footer || defaultFooter);
            } else {
                setHeader(defaultHeader);
                setFooter(defaultFooter);
            }
        })();
    }, []);

    const handleSave = async () => {
        await emailTemplateStore.saveLayoutSettings?.({ header, footer });
        alert(t("emails.layout.saved"));
    };

    return (
        <Paper sx={{ p: 3, maxWidth: 700, mx: "auto", mt: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                {t("emails.layout.title")}
            </Typography>
            <Box component="form" noValidate autoComplete="off">
                <TextField
                    label={t("emails.layout.header")}
                    value={header}
                    onChange={e => setHeader(e.target.value)}
                    multiline
                    rows={6}
                    fullWidth
                    variant="standard"
                    sx={{ mb: 2 }}
                />
                <TextField
                    label={t("emails.layout.footer")}
                    value={footer}
                    onChange={e => setFooter(e.target.value)}
                    multiline
                    rows={6}
                    fullWidth
                    variant="standard"
                    sx={{ mb: 2 }}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleSave}
                    >
                        {t("emails.layout.save")}
                    </Button>
                </Box>
            </Box>
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                {t("emails.layout.preview")}
            </Typography>
            <Paper sx={{ p: 2, mb: 2 }}>
                <div dangerouslySetInnerHTML={{ __html: header }} />
                <div style={{ minHeight: 100, padding: 20, background: "#fafafa" }}>
                    <em>{t("emails.layout.bodyPlaceholder")}</em>
                </div>
                <div dangerouslySetInnerHTML={{ __html: footer }} />
            </Paper>
        </Paper>
    );
}
